<template>
  <div
    v-if="editor"
    class="kv-editor"
    :class="[
      sizeClass,
      `kv-${variant}-input`,
      { 'kv-disabled': disabled, 'kv-error': hasError },
    ]"
    :style="editorStyle"
  >
    <bubble-menu :editor="editor" :tippy-options="{ duration: 100 }">
      <div class="bubble-menu">
        <button @click="toggleBold" :title="t('editor-bold')">
          <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="currentColor">
            <path d="M358-239q-13 0-21.5-8.5T328-269v-422q0-13 8.5-21.5T358-721h122q55 0 95.5 35t40.5 89q0 35-19.5 64.5T544-489q40 10 64 43.5t24 75.5q0 57-43 94t-101 37H358Zm0-28h128q46 0 80-29t34-74q0-45-34-74t-80-29H358v206Zm0-234h121q42 0 73.5-27t31.5-69q0-42-31.5-69T479-693H358v192Z" />
          </svg>
        </button>
        <button @click="toggleItalic" :title="t('editor-italic')">
          <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="currentColor">
            <path d="M253.85-230q-9.37 0-15.84-6.49-6.47-6.49-6.47-15.88 0-9.4 6.47-15.82 6.47-6.43 15.84-6.43h123.07l139.23-410.76H393.08q-9.37 0-15.84-6.49-6.47-6.49-6.47-15.89 0-9.39 6.47-15.82 6.47-6.42 15.84-6.42h287.69q9.37 0 15.84 6.49 6.47 6.49 6.47 15.88 0 9.4-6.47 15.82-6.47 6.43-15.84 6.43H562.31L423.08-274.62h118.46q9.37 0 15.84 6.49 6.47 6.49 6.47 15.89 0 9.39-6.47 15.82-6.47 6.42-15.84 6.42H253.85Z" />
          </svg>
        </button>
        <button @click="toggleUnderline" :title="t('editor-underline')">
          <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="currentColor">
            <path d="M247.69-190q-8.5 0-14.25-5.76t-5.75-14.27q0-8.51 5.75-14.24t14.25-5.73h464.62q8.5 0 14.25 5.76t5.75 14.27q0 8.51-5.75 14.24T712.31-190H247.69ZM480-317.69q-85.62 0-134.31-50.31T297-504.62v-279.76q0-9.29 6.84-15.91 6.85-6.63 16.12-6.63 9.27 0 15.81 6.63 6.54 6.62 6.54 15.91v281.15q0 65.23 36.46 103.31 36.46 38.07 101.23 38.07 64.77 0 101.23-38.07 36.46-38.08 36.46-103.31v-281.15q0-9.29 6.85-15.91 6.84-6.63 16.11-6.63t15.81 6.63q6.54 6.62 6.54 15.91v279.76q0 86.31-48.69 136.62T480-317.69Z" />
          </svg>
        </button>
        <button @click="toggleStrike" :title="t('editor-strikethrough')">
          <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="currentColor">
            <path d="M486-184.62q-56.85 0-104.96-30.03-48.12-30.04-75.27-80.66-3.62-7.54-.58-15.81 3.04-8.26 10.81-11.88 8-3.85 16.73-.81t13.58 11.04q21.84 39.23 58.88 61.85 37.04 22.61 81.27 22.61 55.54 0 98.04-31.61 42.5-31.62 42.5-82.85 0-13.08-2.31-26.15-2.31-13.08-9.23-24.62-2.31-8.46 2.46-15.54 4.77-7.07 13.23-9.38 8.47-2.31 16.04 2.46 7.58 4.77 9.89 13.23 8.46 13.77 11.15 30.23 2.69 16.46 2.69 32.54 0 69-55.96 112.19T486-184.62ZM120-500q-8.54 0-14.27-5.73T100-520q0-8.54 5.73-14.27T120-540h720q8.54 0 14.27 5.73T860-520q0 8.54-5.73 14.27T840-500H120Zm211.85-121.15q-8.54-2.31-13-9.89-4.47-7.58-2.16-16.11 4.23-58.54 53.16-94.85 48.92-36.31 112.15-36.31 42.62 0 80.12 18.08 37.5 18.08 62.65 51.46 4.85 7.08 3.54 15.46-1.31 8.39-8.62 13.23-7.77 5.08-16.5 3.62t-15.04-9q-18.84-24.69-46.53-36.92-27.7-12.24-59.16-12.24-47.77 0-84.69 26.77-36.92 26.77-39.92 71.31-2.31 8.77-9.89 13.23-7.58 4.46-16.11 2.16Z" />
          </svg>
        </button>

        <button @click="setHeading(1)" :title="t('editor-heading1')">
          <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="currentColor">
            <path d="M259.97-300q-8.51 0-14.24-5.75T240-320v-320q0-8.5 5.76-14.25t14.27-5.75q8.51 0 14.24 5.75T280-640v140h200v-140q0-8.5 5.76-14.25t14.27-5.75q8.51 0 14.24 5.75T520-640v320q0 8.5-5.76 14.25T499.97-300q-8.51 0-14.24-5.75T480-320v-140H280v140q0 8.5-5.76 14.25T259.97-300Zm440 0q-8.51 0-14.24-5.75T680-320v-300h-60q-8.5 0-14.25-5.76T600-640.03q0-8.51 5.75-14.24T620-660h67.31q14.31 0 23.5 9.29t9.19 23.02V-320q0 8.5-5.76 14.25T699.97-300Z" />
          </svg>
        </button>
        <button @click="setHeading(2)" :title="t('editor-heading2')">
          <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="currentColor">
            <path d="M179.97-300q-8.51 0-14.24-5.75T160-320v-320q0-8.5 5.76-14.25t14.27-5.75q8.51 0 14.24 5.75T200-640v140h200v-140q0-8.5 5.76-14.25t14.27-5.75q8.51 0 14.24 5.75T440-640v320q0 8.5-5.76 14.25T419.97-300q-8.51 0-14.24-5.75T400-320v-140H200v140q0 8.5-5.76 14.25T179.97-300Zm372.34 0q-13.73 0-23.02-9.29T520-332.31v-103.07q0-26.66 18.98-45.64T584.62-500h150.76q10.77 0 17.7-6.92 6.92-6.93 6.92-17.7v-70.76q0-10.77-6.92-17.7-6.93-6.92-17.7-6.92H540q-8.5 0-14.25-5.76T520-640.03q0-8.51 5.75-14.24T540-660h195.38q26.66 0 45.64 18.98T800-595.38v70.76q0 26.66-18.98 45.64T735.38-460H584.62q-10.77 0-17.7 6.92-6.92 6.93-6.92 17.7V-340h220q8.5 0 14.25 5.76t5.75 14.27q0 8.51-5.75 14.24T780-300H552.31Z" />
          </svg>
        </button>

        <button @click="toggleBulletList" :title="t('editor-bullet_list')">
          <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="currentColor">
            <path d="M342-212q-13 0-21.5-8.5T312-242q0-13 8.5-21.5T342-272h456q13 0 21.5 8.5T828-242q0 13-8.5 21.5T798-212H342Zm0-238q-13 0-21.5-8.5T312-480q0-13 8.5-21.5T342-510h456q13 0 21.5 8.5T828-480q0 13-8.5 21.5T798-450H342Zm0-238q-13 0-21.5-8.5T312-718q0-13 8.5-21.5T342-748h456q13 0 21.5 8.5T828-718q0 13-8.5 21.5T798-688H342Zm-180 0q-13 0-21.5-8.5T132-718q0-13 8.5-21.5T162-748q13 0 21.5 8.5T192-718q0 13-8.5 21.5T162-688Zm0 238q-13 0-21.5-8.5T132-480q0-13 8.5-21.5T162-510q13 0 21.5 8.5T192-480q0 13-8.5 21.5T162-450Zm0 238q-13 0-21.5-8.5T132-242q0-13 8.5-21.5T162-272q13 0 21.5 8.5T192-242q0 13-8.5 21.5T162-212Z" />
          </svg>
        </button>
        <button @click="toggleOrderedList" :title="t('editor-numbered_list')">
          <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="currentColor">
            <path d="M177.69-120q-7.66 0-12.68-5-5.01-5.01-5.01-12.66t5.01-12.69q5.02-5.03 12.68-5.03H260V-210h-42.31q-7.66 0-12.68-5-5.01-5.01-5.01-12.66t5.01-12.69q5.02-5.03 12.68-5.03H260V-300h-82.31q-7.66 0-12.68-5-5.01-5.01-5.01-12.66t5.01-12.69q5.02-5.03 12.68-5.03h90q11.77 0 19.73 7.96 7.96 7.96 7.96 19.73v55.38q0 11.77-7.96 19.73-7.96 7.96-19.73 7.96 11.77 0 19.73 7.97 7.96 7.96 7.96 19.73v49.23q0 11.77-7.96 19.73-7.96 7.96-19.73 7.96h-90Zm6.93-252.31q-9.93 0-17.27-7.34Q160-387 160-396.92V-470q0-11.77 7.96-19.73 7.96-7.96 19.73-7.96H260v-54.62h-82.31q-7.66 0-12.68-5-5.01-5-5.01-12.65 0-7.66 5.01-12.69 5.02-5.04 12.68-5.04h90q11.77 0 19.73 7.96 7.96 7.96 7.96 19.73v70q0 11.77-7.96 19.73-7.96 7.96-19.73 7.96h-72.31v54.62h82.31q7.67 0 12.68 5t5.01 12.65q0 7.66-5.01 12.69-5.01 5.04-12.68 5.04h-93.07Zm53.11-252.31q-7.65 0-12.69-5.01-5.04-5.01-5.04-12.68v-162.31h-42.31q-7.66 0-12.68-5-5.01-5-5.01-12.65t5.01-12.69q5.02-5.04 12.68-5.04h53.85q9.92 0 16.88 6.96 6.96 6.96 6.96 16.89v173.84q0 7.67-5 12.68t-12.65 5.01ZM404.62-220q-8.5 0-14.25-5.76t-5.75-14.27q0-8.51 5.75-14.24t14.25-5.73H780q8.5 0 14.25 5.76t5.75 14.27q0 8.51-5.75 14.24T780-220H404.62Zm0-240q-8.5 0-14.25-5.76t-5.75-14.27q0-8.51 5.75-14.24t14.25-5.73H780q8.5 0 14.25 5.76t5.75 14.27q0 8.51-5.75 14.24T780-460H404.62Zm0-240q-8.5 0-14.25-5.76t-5.75-14.27q0-8.51 5.75-14.24t14.25-5.73H780q8.5 0 14.25 5.76t5.75 14.27q0 8.51-5.75 14.24T780-700H404.62Z" />
          </svg>
        </button>

        <button @click="undo" :title="t('editor-undo')">
          <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="currentColor">
            <path d="M316.15-240q-8.53 0-14.27-5.73-5.73-5.73-5.73-14.27t5.73-14.27q5.74-5.73 14.27-5.73h270.16q62.23 0 106.04-42.69 43.8-42.69 43.8-104.23 0-61.54-43.8-103.85-43.81-42.31-106.04-42.31H276.62l104.46 104.46q6.38 6.39 6.38 14.16 0 7.77-6.38 14.15-6.39 6.39-14.54 6-8.16-.38-13.77-6L222.62-570.46q-5.24-5.23-7.35-10.69-2.12-5.47-2.12-11.93t2.12-11.92q2.11-5.46 7.35-10.69l130.15-130.16q6.38-6.38 14.15-6.38 7.77 0 14.16 6.38 6.38 6.39 6 14.54-.39 8.16-6 13.77L276.62-613.08h309.69q78.54 0 134.19 54.16 55.65 54.15 55.65 132 0 77.84-55.65 132.38Q664.85-240 586.31-240H316.15Z" />
          </svg>
        </button>
        <button @click="redo" :title="t('editor-redo')">
          <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="currentColor">
            <path d="M683.38-573.08H373.69q-62.23 0-106.04 42.31-43.8 42.31-43.8 103.85 0 61.54 43.8 104.23Q311.46-280 373.69-280h270.16q8.53 0 14.27 5.73 5.73 5.73 5.73 14.27t-5.73 14.27q-5.74 5.73-14.27 5.73H373.69q-78.54 0-134.19-54.54-55.65-54.54-55.65-132.38 0-77.85 55.65-132 55.65-54.16 134.19-54.16h309.69L578.92-717.54q-5.61-5.61-6-13.77-.38-8.15 6-14.54 6.39-6.38 14.16-6.38 7.77 0 14.15 6.38l130.15 130.16q5.24 5.23 7.35 10.69 2.12 5.46 2.12 11.92t-2.12 11.93q-2.11 5.46-7.35 10.69L607.23-440.31q-5.61 5.62-13.77 6-8.15.39-14.54-6-6.38-6.38-6.38-14.15 0-7.77 6.38-14.16l104.46-104.46Z" />
          </svg>
        </button>
      </div>
    </bubble-menu>

    <editor-content :editor="editor" spellcheck="false" />
  </div>
</template>

<script>
import { Editor, EditorContent, BubbleMenu } from "@tiptap/vue-2";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import Underline from '@tiptap/extension-underline'


export default {
  name: "KvEditor",
  props: {
    value: {
      type: Object,
      default: () => ({
        html: "",
        text: "",
        isEmpty: true,
      }),
    },
    placeholder: {
      type: String,
      default: "",
    },
    variant: {
      type: String,
      default: "outlined",
    },
    size: {
      type: String,
      default: "regular",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    hasError: {
      type: Boolean,
      default: false,
    },
    height: {
      type: [String, Number],
      default: null,
    },
    maxHeight: {
      type: [String, Number],
      default: null,
    },
  },
  components: {
    EditorContent,
    BubbleMenu,
  },
  data() {
    return {
      editor: null,
    };
  },
  mounted() {
    this.editor = new Editor({
      extensions: [
        StarterKit,
        Underline,
        Placeholder.configure({
          placeholder: this.resolvedPlaceholder,
        }),],
      content: this.value?.html || "",
      onUpdate: ({ editor }) => {
        this.$emit("input", {
          html: editor.getHTML(),
          text: editor.getText(),
          empty: editor.isEmpty,
        });
      },
    });
  },
  beforeDestroy() {
    if (this.editor) {
      this.editor.destroy();
    }
  },

  methods: {
    t(term, replaces) {
      return this.$root.t(term, replaces);
    },
    toggleBold() {
      this.editor?.chain().focus().toggleBold().run();
    },
    toggleItalic() {
      this.editor?.chain().focus().toggleItalic().run();
    },
    toggleUnderline() {
      this.editor?.chain().focus().toggleUnderline().run();
    },
    toggleStrike() {
      this.editor?.chain().focus().toggleStrike().run();
    },
    setHeading(level) {
      this.editor?.chain().focus().toggleHeading({ level }).run();
    },
    toggleBulletList() {
      this.editor?.chain().focus().toggleBulletList().run();
    },
    toggleOrderedList() {
      this.editor?.chain().focus().toggleOrderedList().run();
    },
    undo() {
      this.editor?.chain().focus().undo().run();
    },
    redo() {
      this.editor?.chain().focus().redo().run();
    },
  },
  computed: {
    sizeClass() {
      return `kv-${this.size}-input`;
    },
    resolvedPlaceholder() {
      return this.placeholder || this.t("inputs-editor-placeholder");
    },
    editorStyle() {
      const normalize = v =>
        typeof v === "number" ? `${v}px` : v;

      return {
        height: this.height ? normalize(this.height) : null,
        maxHeight: this.maxHeight ? normalize(this.maxHeight) : null,
      };
    },
  },
  watch: {
    value: {
      deep: true,
      handler(newValue) {
        if (!this.editor) return

        const currentHTML = this.editor.getHTML()
        const incomingHTML = newValue?.html || ''

        if (currentHTML !== incomingHTML) {
          this.editor.commands.setContent(incomingHTML, false)
        }
      }
    }
  }

};
</script>

<style lang="less">
.kv-editor {
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: #ffffff;
  padding: 10px;
  overflow-y: auto;
  width: 100%;

  &.kv-tinted-input {
    border-color: #2aaa4c;
    background-color: #f3faf4;

    .ProseMirror {
      color: #1d7836;
    }
  }

  &.kv-outlined-input {
    border-color: #e5e7eb;
    background-color: #ffffff;

    .ProseMirror {
      color: #1f2a37;
    }
  }

  &.kv-small-input .ProseMirror {
    font-size: 13px;
    min-height: 96px;
  }

  &.kv-large-input .ProseMirror {
    font-size: 14px;
    min-height: 120px;
  }

  &.kv-error {
    border-color: #f87171;
  }

  &.kv-disabled {
    opacity: 0.6;
    pointer-events: none;
  }

  &:focus-within {
    border-color: #2aaa4c;
    box-shadow: 0 0 0 2px rgba(42, 170, 76, 0.14);
  }


  /* Bubble container */
  .bubble-menu {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px;
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    box-shadow: 0 4px 6px -2px rgba(16, 24, 40, 0.06),
      0 10px 15px -3px rgba(16, 24, 40, 0.08);
  }

  /* Button base */
  .bubble-menu button {
    all: unset;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: #344054;
    /* gray-700 */
    transition: background-color 0.15s ease, color 0.15s ease;
  }

  /* Icon */
  .bubble-menu button svg {
    width: 24px;
    height: 24px;
    stroke-width: 2;
  }

  /* Hover */
  .bubble-menu button:hover {
    background: #f2f4f7;
    /* gray-100 */
  }

  /* Active (quando formatado) */
  .bubble-menu button.kv-active {
    background: #eff8ff;
    /* blue-50 */
    color: #1570ef;
    /* blue-600 */
  }

  /* Disabled */
  .bubble-menu button:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  /* Divider (opcional) */
  .bubble-menu .divider {
    width: 1px;
    height: 20px;
    background: #e5e7eb;
    margin: 0 4px;
  }

  /* Editor content */
  .ProseMirror {
    color: #1f2a37;
    outline: none;
    min-height: 120px;
    font-size: 14px;
    line-height: 1.5;
    font-family: inherit;

    * {
      margin: 0 !important;
    }

    p.is-editor-empty:first-child::before {
      content: attr(data-placeholder);
      color: #9ca3af;
      pointer-events: none;
      float: left;
      height: 0;
    }
  }
}
</style>
